import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import ShareModal from '../components/ShareModal';
import StoriesBar from '../components/StoriesBar';
import { FeedSkeleton } from '../components/SkeletonLoader';
import EditPostModal from '../components/EditPostModal';
import PostCard from '../components/PostCard';
import PostComposer from '../components/PostComposer';
import { Users, Globe, Compass } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SCOPE_KEY = 'os_feed_scope';

const Feed = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});
  const [sharePost, setSharePost] = useState(null);
  const [editPost, setEditPost] = useState(null);
  // Brief 27 oct A.5 — onglets Abonnements / Tout. Par défaut « Tout » tant que
  // l'utilisateur suit < 3 personnes, « Abonnements » ensuite (choix mémorisé).
  const [scope, setScope] = useState(() => sessionStorage.getItem(SCOPE_KEY) || 'auto');
  const [followingCount, setFollowingCount] = useState(null);
  const [switching, setSwitching] = useState(false);

  // Brief LOT2.2 — ?post={id} : scroll + highlight du post ciblé par une notification
  const [highlightPostId, setHighlightPostId] = useState(null);
  const deepLinkHandled = useRef(false);
  const [bookedEventIds, setBookedEventIds] = useState(() => new Set());

  const loadFeed = useCallback(async (requestedScope, silent = false) => {
    const s = requestedScope || scope;
    if (!silent) setSwitching(true);
    try {
      const response = await axios.get(`${API}/feed`, { params: { scope: s }, withCredentials: true });
      setPosts(response.data.posts);
      setFollowingCount(response.data.following_count ?? 0);
      if (response.data.scope && response.data.scope !== scope) setScope(response.data.scope);
      return response.data.posts;
    } catch (error) {
      console.error('Error loading feed:', error);
      return [];
    } finally {
      setLoading(false);
      setSwitching(false);
    }
  }, [scope]);

  useEffect(() => {
    loadFeed(scope);
    loadMyBookedEventIds();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(() => { loadFeed(scope, true); }, 15000);
    return () => clearInterval(interval);
  }, [scope, loadFeed]);

  const changeScope = (next) => {
    if (next === scope) return;
    sessionStorage.setItem(SCOPE_KEY, next);
    setScope(next);
    loadFeed(next);
  };

  useEffect(() => {
    const targetId = searchParams.get('post');
    if (!targetId || loading || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const wantComments = searchParams.get('comments') === '1';
    (async () => {
      let present = posts.some((p) => p.post_id === targetId);
      if (!present) {
        try {
          const { data } = await axios.get(`${API}/posts/${targetId}`, { withCredentials: true });
          setPosts((prev) => [data, ...prev.filter((p) => p.post_id !== targetId)]);
          present = true;
        } catch (e) {
          console.error('Deep-link post fetch failed:', e);
        }
      }
      if (present) {
        setHighlightPostId(targetId);
        if (wantComments) setExpandedComments((prev) => ({ ...prev, [targetId]: true }));
        setTimeout(() => {
          document.getElementById(`post-${targetId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 250);
        setTimeout(() => setHighlightPostId(null), 4500);
      }
      const next = new URLSearchParams(searchParams);
      next.delete('post');
      next.delete('comments');
      setSearchParams(next, { replace: true });
    })();
  }, [loading, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMyBookedEventIds = async () => {
    try {
      if (!user?.user_id) return;
      const { data } = await axios.get(`${API}/bookings?role=client`, { withCredentials: true });
      const ids = new Set();
      (data?.bookings || []).forEach((b) => {
        if (b.event_id && ['confirmed', 'pending'].includes(b.status)) ids.add(b.event_id);
      });
      setBookedEventIds(ids);
    } catch {}
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Supprimer cette publication ?')) return;
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
    } catch {}
  };

  if (loading) {
    return <FeedSkeleton />;
  }

  const tabs = [
    { key: 'all', label: 'Tout', icon: Globe, hint: 'Toute la plateforme' },
    { key: 'following', label: 'Abonnements', icon: Users, hint: followingCount ? `${followingCount} personne${followingCount > 1 ? 's' : ''} suivie${followingCount > 1 ? 's' : ''}` : 'Les personnes que tu suis' },
  ];

  return (
    <div className="space-y-6" data-testid="feed-page">
      <StoriesBar />

      <PostComposer variant="feed" onPosted={() => loadFeed(scope, true)} />

      {/* A.5 — filtre Abonnements / Tout */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700" data-testid="feed-scope-tabs" role="tablist">
        {tabs.map(({ key, label, icon: Icon, hint }) => {
          const active = scope === key;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={() => changeScope(key)}
              title={hint}
              className={`relative flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                active ? 'text-purple-700 dark:text-purple-300' : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
              }`}
              data-testid={`feed-tab-${key}`}
            >
              <Icon className="h-4 w-4" />
              {label}
              {active && <span className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-gradient-to-r from-purple-600 to-pink-600" />}
            </button>
          );
        })}
        {switching && <span className="ml-auto text-xs text-gray-400 pr-2" data-testid="feed-switching">Chargement…</span>}
      </div>

      {posts.length === 0 ? (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="py-12 text-center" data-testid="empty-feed">
            {scope === 'following' ? (
              <>
                <Users className="h-10 w-10 text-purple-300 mx-auto mb-3" />
                <p className="font-semibold text-gray-800 dark:text-gray-100">
                  {followingCount ? 'Les personnes que tu suis n’ont encore rien publié' : 'Tu ne suis encore personne'}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-5">
                  Suis des experts pour composer ton fil, ou passe sur « Tout » pour voir toute la plateforme.
                </p>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <Button onClick={() => navigate('/app/discover')} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white" data-testid="empty-feed-discover-btn">
                    <Compass className="h-4 w-4 mr-2" />Découvrir des experts
                  </Button>
                  <Button variant="outline" onClick={() => changeScope('all')} data-testid="empty-feed-all-btn">Voir tout</Button>
                </div>
              </>
            ) : (
              <>
                <Globe className="h-10 w-10 text-purple-300 mx-auto mb-3" />
                <p className="font-semibold text-gray-800 dark:text-gray-100">Aucune publication pour le moment</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Sois le premier à partager quelque chose !</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        posts.map((post, index) => (
          <div key={post.post_id} className="animate-fadeInUp" style={{ animationDelay: `${Math.min(index * 60, 300)}ms` }}>
            <PostCard
              post={post}
              variant="feed"
              currentUser={user}
              bookedEventIds={bookedEventIds}
              canManage={!!post.is_own}
              onDelete={handleDeletePost}
              onShare={(p) => setSharePost(p)}
              onEdit={(p) => setEditPost(p)}
              expandedComments={expandedComments}
              setExpandedComments={setExpandedComments}
              highlighted={highlightPostId === post.post_id}
            />
          </div>
        ))
      )}

      {editPost && (
        <EditPostModal
          isOpen={!!editPost}
          post={editPost}
          onClose={() => setEditPost(null)}
          onSaved={(updated) => {
            setPosts((prev) => prev.map((p) => (p.post_id === updated.post_id ? { ...p, ...updated } : p)));
            setEditPost(null);
          }}
        />
      )}

      {sharePost && (
        <ShareModal
          isOpen={!!sharePost}
          onClose={() => setSharePost(null)}
          shareType="post"
          shareData={{
            id: sharePost.post_id,
            title: `Publication de ${sharePost.author_name}`,
            description: sharePost.content?.substring(0, 100) + (sharePost.content?.length > 100 ? '...' : ''),
          }}
        />
      )}
    </div>
  );
};

export default Feed;
